import { Map } from 'app/design/primitives/map';
import type { Address } from 'app/types/address';
import { useUISize } from 'app/utils/web-helpers';
import { Fragment } from 'react';

type SearchAddress = Pick<Address, 'id' | 'latitude' | 'longitude' | 'radiusCenterLatitude' | 'radiusCenterLongitude'>;

export function getSearchAddressesRegion(addresses: SearchAddress[], radius: number) {
  const centers = addresses.map((address) => ({
    latitude: address.radiusCenterLatitude ?? address.latitude,
    longitude: address.radiusCenterLongitude ?? address.longitude,
  }));
  if (!centers.length) return undefined;

  const latitudePadding = radius / 111_320;
  const north = Math.max(...centers.map((center) => center.latitude + latitudePadding));
  const south = Math.min(...centers.map((center) => center.latitude - latitudePadding));
  const longitudePadding = (latitude: number) => latitudePadding / Math.max(Math.cos((latitude * Math.PI) / 180), 0.1);
  const east = Math.max(...centers.map((center) => center.longitude + longitudePadding(center.latitude)));
  const west = Math.min(...centers.map((center) => center.longitude - longitudePadding(center.latitude)));

  return {
    latitude: (north + south) / 2,
    longitude: (east + west) / 2,
    latitudeDelta: (north - south) * 1.25,
    longitudeDelta: (east - west) * 1.25,
  };
}

// With multiple addresses the API searches around each address's own center.
// Preview all of them without offering a single draggable center that cannot be saved.
export function SearchAddressesMap({ addresses, radius }: { addresses: SearchAddress[]; radius: number }) {
  const { isSmallUI } = useUISize();
  const validAddresses = addresses.filter(
    (address) => Number.isFinite(address.latitude) && Number.isFinite(address.longitude),
  );
  const region = getSearchAddressesRegion(validAddresses, radius);
  if (!region) return null;

  return (
    <Map
      region={region}
      width="100%"
      aspectRatio={isSmallUI ? 1.5 : 16 / 9}
      borderRadius="radius.lg"
      overflow="hidden"
      rotateEnabled={false}
    >
      {validAddresses.map((address) => (
        <Fragment key={address.id}>
          <Map.Marker coordinate={address} />
          <Map.Circle
            center={{
              latitude: address.radiusCenterLatitude ?? address.latitude,
              longitude: address.radiusCenterLongitude ?? address.longitude,
            }}
            radius={radius}
          />
        </Fragment>
      ))}
    </Map>
  );
}
